import { computed, type Ref } from "vue";
import {
  diffMergePatch,
  loadAccountConfigs,
  setAccountConfigForTests,
  useAccountConfig,
  type JsonObject,
} from "@blpde/connect";

/**
 * The account's search settings (I-2348): which modules the universal search
 * asks, in which order, and how. Stored in the account config key `search`
 * (`/v1/account/configs/search`, I-2510), so a tenant or company can ship
 * defaults and every device of the account sees the same order; a change
 * lands on the session's profile layer, only the fields that changed. The
 * helper's boot cache (`formica.config.search`) paints before master answers.
 */
export interface SearchSettings {
  /** Module keys, most important first. Modules missing here follow in the default order. */
  moduleOrder: string[];
  /** Modules the search does not ask. */
  disabledModules: string[];
  /** Kind keys (`erp.quote`) the search leaves out. */
  disabledKinds: string[];
  /** Kind order inside a module, by module key; kinds missing here keep the module's order. */
  kindOrder: Record<string, string[]>;
  /** Modules searched by numbers and names only, without full text. */
  fulltextOff: string[];
  /** Hits per kind (1–20). */
  limit: number;
  /** Lift exact hits into „Beste Treffer“. */
  topHits: boolean;
  /** Offer „Assistent fragen“ when nothing matched. */
  askAssistant: boolean;
  /** Remember an accepted „Meinten Sie“ suggestion as a personal correction. */
  learnCorrections: boolean;
  /** How a chip in every query input is changed. */
  chipInteraction: "direct" | "menu";
  /** The words on those chips. */
  chipWording: "logic" | "plain";
  /** Extra keywords per kind key, lower case. */
  keywords: Record<string, string[]>;
}

/** The order without a setting: ERP first (the maintainer's call, I-2348). */
export const DEFAULT_MODULE_ORDER = [
  "erp",
  "mail",
  "calendar",
  "drive",
  "wiki",
  "chat",
  "master",
];

export function defaultSearchSettings(): SearchSettings {
  return {
    moduleOrder: [...DEFAULT_MODULE_ORDER],
    disabledModules: [],
    disabledKinds: [],
    kindOrder: {},
    fulltextOff: [],
    limit: 5,
    topHits: true,
    askAssistant: true,
    learnCorrections: true,
    chipInteraction: "direct",
    chipWording: "logic",
    keywords: {},
  };
}

const strings = (v: unknown): string[] =>
  Array.isArray(v)
    ? [...new Set(v.filter((s): s is string => typeof s === "string" && !!s))]
    : [];

const keywordList = (v: unknown): string[] =>
  strings(v)
    .map((k) => k.trim().toLowerCase())
    .filter((k) => k && !/\s/.test(k));

/** Reads a stored document; anything malformed falls back to the default. */
export function normalizeSearchSettings(raw: unknown): SearchSettings {
  const d = defaultSearchSettings();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return d;
  const r = raw as Record<string, unknown>;
  const order = strings(r.moduleOrder);
  const kindOrder: Record<string, string[]> = {};
  if (
    r.kindOrder &&
    typeof r.kindOrder === "object" &&
    !Array.isArray(r.kindOrder)
  ) {
    for (const [module, kinds] of Object.entries(
      r.kindOrder as Record<string, unknown>,
    )) {
      const list = strings(kinds);
      if (list.length) kindOrder[module] = list;
    }
  }
  const keywords: Record<string, string[]> = {};
  if (
    r.keywords &&
    typeof r.keywords === "object" &&
    !Array.isArray(r.keywords)
  ) {
    for (const [kind, words] of Object.entries(
      r.keywords as Record<string, unknown>,
    )) {
      const list = keywordList(words);
      if (list.length) keywords[kind] = list;
    }
  }
  const limit = Number(r.limit);
  return {
    moduleOrder: [
      ...order,
      ...DEFAULT_MODULE_ORDER.filter((m) => !order.includes(m)),
    ],
    disabledModules: strings(r.disabledModules),
    disabledKinds: strings(r.disabledKinds),
    kindOrder,
    fulltextOff: strings(r.fulltextOff),
    limit:
      Number.isInteger(limit) && limit >= 1 && limit <= 20 ? limit : d.limit,
    topHits: typeof r.topHits === "boolean" ? r.topHits : d.topHits,
    askAssistant:
      typeof r.askAssistant === "boolean" ? r.askAssistant : d.askAssistant,
    learnCorrections:
      typeof r.learnCorrections === "boolean"
        ? r.learnCorrections
        : d.learnCorrections,
    chipInteraction:
      r.chipInteraction === "direct" || r.chipInteraction === "menu"
        ? r.chipInteraction
        : d.chipInteraction,
    chipWording:
      r.chipWording === "logic" || r.chipWording === "plain"
        ? r.chipWording
        : d.chipWording,
    keywords,
  };
}

/** Rank of a module in the account's order; unknown modules go last, alphabetically. */
export function moduleRank(settings: SearchSettings, module: string): number {
  const i = settings.moduleOrder.indexOf(module);
  return i >= 0 ? i : settings.moduleOrder.length;
}

/** Orders kinds of a module by the account's kind order, the rest in catalog order. */
export function orderKinds<T extends { key: string }>(
  settings: SearchSettings,
  module: string,
  kinds: readonly T[],
): T[] {
  const order = settings.kindOrder[module] ?? [];
  const rank = (k: T) => {
    const i = order.indexOf(k.key);
    return i >= 0 ? i : order.length;
  };
  return kinds
    .map((k, i) => ({ k, i }))
    .sort((a, b) => rank(a.k) - rank(b.k) || a.i - b.i)
    .map(({ k }) => k);
}

// ── Store ──────────────────────────────────────────────────────────────────

export const SEARCH_CONFIG_KEY = "search";

const config = useAccountConfig<JsonObject>(SEARCH_CONFIG_KEY, {
  immediate: false,
});

const state = computed<SearchSettings>(() =>
  normalizeSearchSettings(config.effective.value),
);

/** Pulls the account's settings once a session exists. Never throws. */
export async function hydrateSearchSettings(): Promise<void> {
  await loadAccountConfigs([SEARCH_CONFIG_KEY]);
}

/**
 * Takes the settings (normalised) and writes what changed to the session's
 * profile layer — debounced by the config helper.
 */
export function saveSearchSettings(next: SearchSettings): void {
  const patch = diffMergePatch(
    state.value as unknown as JsonObject,
    normalizeSearchSettings(next) as unknown as JsonObject,
  );
  if (Object.keys(patch).length) void config.patch(patch);
}

/** Back to the default (the tenant's or company's, if one is set). */
export async function resetSearchSettings(): Promise<void> {
  await config.reset();
}

export function useSearchSettings(): {
  settings: Readonly<Ref<SearchSettings>>;
} {
  return { settings: state };
}

/** Test seam. */
export function resetSearchSettingsForTests(
  next: SearchSettings = defaultSearchSettings(),
): void {
  setAccountConfigForTests(
    SEARCH_CONFIG_KEY,
    next as unknown as JsonObject,
  );
}
